import React, { useEffect, useState, useRef } from 'react';
import { XIcon } from 'lucide-react';
interface TooltipProps {
  children: React.ReactNode;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  showOnFirstVisit?: boolean;
  tooltipKey?: string;
}
export function Tooltip({
  children,
  content,
  position = 'top',
  showOnFirstVisit = false,
  tooltipKey
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const autoShowTimeoutRef = useRef<NodeJS.Timeout>();
  const autoDismissRef = useRef<NodeJS.Timeout>();
  // Auto-show on first visit, then auto-dismiss after 4 seconds
  useEffect(() => {
    if (showOnFirstVisit && tooltipKey) {
      const dismissed = localStorage.getItem(`tooltip-dismissed-${tooltipKey}`);
      if (!dismissed) {
        autoShowTimeoutRef.current = setTimeout(() => {
          setIsVisible(true);
          // Auto-dismiss after 4 seconds
          autoDismissRef.current = setTimeout(() => {
            setIsVisible(false);
            if (tooltipKey) {
              localStorage.setItem(`tooltip-dismissed-${tooltipKey}`, 'true');
            }
          }, 4000);
        }, 1000);
      }
    }
    return () => {
      clearTimeout(autoShowTimeoutRef.current);
      clearTimeout(hoverTimeoutRef.current);
      clearTimeout(autoDismissRef.current);
    };
  }, [showOnFirstVisit, tooltipKey]);
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    clearTimeout(autoDismissRef.current);
    if (tooltipKey) {
      localStorage.setItem(`tooltip-dismissed-${tooltipKey}`, 'true');
    }
  };
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeoutRef.current);
    clearTimeout(autoDismissRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 200);
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    clearTimeout(autoDismissRef.current);
    setIsVisible(false);
  };
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };
  const arrowClasses = {
    top: 'bottom-[-4px] left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900',
    bottom: 'top-[-4px] left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900',
    left: 'right-[-4px] top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900',
    right: 'left-[-4px] top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900'
  };
  return <div className="relative inline-block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-1114">
      {children}
      {isVisible && <div className={`absolute z-50 w-max max-w-xs px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md shadow-lg pointer-events-none ${positionClasses[position]}`} role="tooltip" data-id="element-1115">
          <div className="flex items-start gap-2" data-id="element-1116">
            <span data-id="element-1117">{content}</span>
            {showOnFirstVisit && <button onClick={handleDismiss} className="pointer-events-auto text-gray-400 hover:text-white transition-colors flex-shrink-0 mt-0.5" aria-label="Dismiss tooltip" data-id="element-1118">
                <XIcon className="w-3.5 h-3.5" data-id="element-1119" />
              </button>}
          </div>
          <div className={`absolute w-0 h-0 border-[5px] ${arrowClasses[position]}`} data-id="element-1120" />
        </div>}
    </div>;
}